import { AxiosRequestConfig, AxiosResponse } from 'axios'
import _forEach from 'lodash/forEach'

import type { IAxiosResponseData } from '../../types/api/axiosResponse.d'
import type {
  ICountriesResponse,
  IEventResponse,
  IRSVPRequestData,
  IRSVPResponse,
  IStatesResponse,
  ITicketData,
  ITicketsAdaptedResponse,
  ITicketsResponse,
  ITimeslotsAdaptedResponse,
  IWaitingListRequestData,
  IWaitingListResponse,
} from '../../types/api/common.d'
import { getQueryVariable } from '../../utils/getQueryVariable'
import { isBrowser } from '../../utils/isBrowser'
import { storage } from '../../utils/storage'
import { publicRequest } from './publicRequest'

export const getEvent = async (
  id: string | number,
  pk?: string
): Promise<IEventResponse> => {
  let referralValue = ''
  let invitationHash
  if (isBrowser) {
    const params = new URL(`${window.location}`)
    const referralId = params.searchParams.get('ttf_r') || ''
    const referral_key = storage.getItem('referral_key')
    const [, referralIdlocal] = (referral_key || '').split('.')
    referralValue = referralId || (referralIdlocal ?? '')
    invitationHash = params.searchParams.get('invitation-hash') || null
  }

  const response: AxiosResponse<IEventResponse, AxiosRequestConfig> =
    await publicRequest.get(`v1/event/${id}`, {
      params: { pk, invitationHash },
      headers: {
        ...publicRequest.defaults.headers.common,
        'Referer-Url': isBrowser && typeof document !== 'undefined' ? document.referrer : '',
        'Referrer-Id': isBrowser ? referralValue : '',
      },
    })
  return response.data
}

export const getTickets = async (
  eventId: string | number,
  promoCode: string,
  pk?: string
): Promise<ITicketsAdaptedResponse> => {
  const invitationHash = getQueryVariable('invitation-hash')
  const referralId = getQueryVariable('ttf_r')

  if (referralId) {
    storage.setItem('promoter_id', referralId)
  }

  const promoterId = storage.getItem('promoter_id')
  const params = { pk } as Record<string, string | number | undefined>

  if (invitationHash) {
    params['invitation-hash'] = invitationHash
  }

  const headers = publicRequest.defaults.headers.common

  if (promoterId) {
    headers['Promotion-Event'] = String(eventId)
    headers['Referrer-Id'] = isBrowser ? promoterId : ''
  }

  if (promoCode) {
    headers['Promotion-Event'] = String(eventId)
    headers['Promotion-Code'] = promoCode
    storage.removeItem('promoter_id')
  }

  const response: AxiosResponse<ITicketsResponse, AxiosRequestConfig> =
    await publicRequest.get(`v1/event/${eventId}/tickets/`, {
      params,
      headers,
    })

  const { tickets } = response.data.data.attributes
  const ticketsArr: ITicketData[] = []
  _forEach(tickets, (item) => {
    ticketsArr.push(item)
  })
  const adaptedResponse: AxiosResponse<ITicketsAdaptedResponse, AxiosRequestConfig> = {
    ...response,
    data: {
      ...response.data,
      data: {
        ...response.data.data,
        attributes: {
          ...response.data.data.attributes,
          tickets: ticketsArr,
        },
      },
    },
  }

  return adaptedResponse.data
}

export const getTimeSlotsByDate = async (
  eventId: string | number,
  date: string,
  promoCode?: string,
  pk?: string
): Promise<ITimeslotsAdaptedResponse> => {
  const invitationHash = getQueryVariable('invitation-hash')
  const referralId = getQueryVariable('ttf_r')

  if (referralId) {
    storage.setItem('promoter_id', referralId)
  }

  const promoterId = storage.getItem('promoter_id')
  const params = { pk } as Record<string, string | number | undefined>

  if (invitationHash) {
    params['invitation-hash'] = invitationHash
  }

  const headers = publicRequest.defaults.headers.common

  if (promoterId) {
    headers['Promotion-Event'] = String(eventId)
    headers['Referrer-Id'] = isBrowser ? promoterId : ''
  }

  if (promoCode) {
    headers['Promotion-Event'] = String(eventId)
    headers['Promotion-Code'] = promoCode
    storage.removeItem('promoter_id')
  }

  const response: AxiosResponse<ITimeslotsAdaptedResponse, AxiosRequestConfig> =
    await publicRequest.get(`v1/event/${eventId}/timeslots`, {
      params: {
        date,
        remove_past_slots: true,
        ...params,
      },
      headers,
    })
  return response.data
}

export const getCountries = async (): Promise<ICountriesResponse> => {
  const response: AxiosResponse<ICountriesResponse, AxiosRequestConfig> =
    await publicRequest.get('/countries/list')
  return response.data
}

export const getStates = async (countryId: string | number): Promise<IStatesResponse> => {
  const response: AxiosResponse<IStatesResponse, AxiosRequestConfig> =
    await publicRequest.get(`/countries/${countryId}/states/`)
  return response.data
}

export const sendRSVPInfo = async (
  eventId: number,
  data: { data: IRSVPRequestData }
): Promise<IRSVPResponse> => {
  const response: AxiosResponse<IRSVPResponse, AxiosRequestConfig> =
    await publicRequest.post(`v1/event/${eventId}/send-rsvp-info`, data)

  return response.data
}

export const addToWaitingList = async (
  id: number,
  data: IWaitingListRequestData
): Promise<IWaitingListResponse> => {
  const response: AxiosResponse<IWaitingListResponse, AxiosRequestConfig> =
    await publicRequest.post(`v1/event/${id}/add_to_waiting_list`, {
      data,
    })

  return response.data
}

export const postReferralVisits = async (
  eventId: string,
  referralId: string
): Promise<IAxiosResponseData> => {
  const response: AxiosResponse<IAxiosResponseData, AxiosRequestConfig> =
    await publicRequest.post(`v1/event/${eventId}/referrer/`, {
      referrer: `${referralId}`,
    })

  return response.data
}

export const validatePhoneNumber = async (phone: string): Promise<IAxiosResponseData> => {
  const response: AxiosResponse<IAxiosResponseData, AxiosRequestConfig> =
    await publicRequest.get(`/v1/account/validate_phone?phone=${phone}`)

  return response.data
}
